
import React, { useState } from "react";
import { format } from "date-fns";
import { useHouses } from "@/hooks/useHouses";
import { useReadings } from "@/hooks/useReadings";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";

// Import our new components
import { HouseSelector } from "@/components/calculator/HouseSelector";
import { MonthSelector } from "@/components/calculator/MonthSelector";
import { ElectricityCalculator } from "@/components/calculator/ElectricityCalculator";
import { WaterCalculator } from "@/components/calculator/WaterCalculator";
import { CommonExpensesCalculator } from "@/components/calculator/CommonExpensesCalculator";
import { AccessDeniedAlert } from "@/components/calculator/AccessDeniedAlert";

const Calculator = () => {
  const [selectedHouse, setSelectedHouse] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>(
    format(new Date(), "yyyy-MM")
  );
  
  // State for electricity calculation
  const [electricityReading1, setElectricityReading1] = useState<string>("");
  const [electricityReading2, setElectricityReading2] = useState<string>("");
  const [electricityResult, setElectricityResult] = useState("");
  
  // State for water calculation
  const [waterReading1, setWaterReading1] = useState<string>("");
  const [waterReading2, setWaterReading2] = useState<string>("");
  const [waterResult, setWaterResult] = useState("");
  
  // State for common expenses
  const [commonExpenseResult, setCommonExpenseResult] = useState("");

  // Fetch houses
  const { houses } = useHouses();
  
  // Fetch readings
  const { data: electricityReadings = [] } = useReadings(selectedHouse, "electricity");
  const { data: waterReadings = [] } = useReadings(selectedHouse, "water");

  // Get current user
  const { user } = useAuth();

  // Check if user is admin
  const { data: isAdmin = false, isLoading: isAdminLoading } = useQuery({
    queryKey: ['user-role-admin', user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      const { data: hasRole, error } = await supabase.rpc('has_role', {
        _role: 'admin'
      });

      if (error) {
        console.error('Error checking admin role:', error);
        return false;
      }

      return hasRole;
    },
    enabled: !!user,
  });

  // Show loading state while checking admin status
  if (isAdminLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // If not admin, show access denied message
  if (!isAdmin) {
    return <AccessDeniedAlert />;
  }

  // Admin view - show the calculator
  return (
    <div className="space-y-6">
      <div className="flex flex-col">
        <h1 className="text-3xl font-bold text-gray-900">Calculadora de Gastos</h1>
        <p className="text-muted-foreground">Calcule sus gastos de servicios y gastos comunes</p>
      </div>

      {/* House Selection */}
      <HouseSelector 
        houses={houses} 
        selectedHouse={selectedHouse} 
        setSelectedHouse={setSelectedHouse} 
      />

      {/* Month Selection */}
      <MonthSelector 
        selectedMonth={selectedMonth} 
        setSelectedMonth={setSelectedMonth} 
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Electricity Section */}
        <ElectricityCalculator 
          electricityReadings={electricityReadings}
          electricityReading1={electricityReading1}
          setElectricityReading1={setElectricityReading1}
          electricityReading2={electricityReading2}
          setElectricityReading2={setElectricityReading2}
          electricityResult={electricityResult}
          setElectricityResult={setElectricityResult}
          selectedHouse={selectedHouse}
          selectedMonth={selectedMonth}
        />

        {/* Water Section */}
        <WaterCalculator 
          waterReadings={waterReadings}
          waterReading1={waterReading1}
          setWaterReading1={setWaterReading1}
          waterReading2={waterReading2}
          setWaterReading2={setWaterReading2}
          waterResult={waterResult}
          setWaterResult={setWaterResult}
          selectedHouse={selectedHouse}
          selectedMonth={selectedMonth}
        />

        {/* Common Expenses Section */}
        <CommonExpensesCalculator 
          commonExpenseResult={commonExpenseResult}
          setCommonExpenseResult={setCommonExpenseResult}
          selectedHouse={selectedHouse}
          selectedMonth={selectedMonth}
        />
      </div>
    </div>
  );
};

export default Calculator;
